<template>
    <v-text-field
        :key="item.label"
        ref="amount"
        v-model="displayValue"
        :prepend-icon="mdiCurrencyPhp"
        color="#009688"
        clearable
        dense
        outlined
        :disabled="!isSelected"
        label="Price"
        :rules="[rules.format].concat([ !item.value || item.value && !!item.price || 'Price is required'])"
        :error-messages="errorMessage"
        @keydown.enter.prevent
        @keyup.enter.prevent
        @input="handleInput"
        @keypress="restrictInput"
        @blur="handleBlur"
    />
</template>

  <script>
import { mdiCurrencyPhp } from '@mdi/js'

export default {
    name: 'DecimalInput',
    props: ['item', 'isSelected', 'value'],
    data() {
        return {
            mdiCurrencyPhp,
            displayValue: '',
            errorMessage: '',
            rules: {
                format: value => {
                    // Check if empty
                    if (!value) return true

                    // Match these formats:
                    // 1. Whole numbers: [1-9][0-9]*
                    // 2. Numbers with exactly 2 decimal places: [1-9][0-9]*\.[0-9]{2}
                    // 3. Numbers starting with decimal: \.[0-9]{1,2}
                    const pattern = /^(0|[1-9]\d*)?(\.\d{1,2})?$/
                    return pattern.test(value) || 'Enter a valid number format'
                }
            }
        }
    },
    watch: {
        'item.value'(newVal) {
            if (!newVal) {
                this.$nextTick(()=> {
                    if (this.$refs)
                        if (this.$refs.amount) {
                            this.$refs.amount.reset()
                            this.errorMessage = ''
                        }
                // this.item.price = ''
                // this.displayValue = ''
                })
            }
        },
        value: {
            immediate: true,
            handler(newVal) {
                if (newVal !== undefined && newVal !== null) {
                    const strValue = String(newVal)
                    if (strValue) {
                        try {
                            // Check if it's a whole number
                            if (/^[1-9]\d*$/.test(strValue)) {
                                this.displayValue = strValue
                                return
                            }

                            // Check if it starts with a decimal point
                            if (/^\.\d{1,2}$/.test(strValue)) {
                                // Keep as is, or pad with a zero if needed
                                if (strValue.length === 2) { // Like ".5"
                                    this.displayValue = strValue
                                } else {
                                    this.displayValue = strValue
                                }
                                return
                            }

                            // Otherwise, format with 2 decimal places
                            const numValue = parseFloat(strValue)
                            if (!isNaN(numValue)) {
                                if (strValue.includes('.')) {
                                    const parts = strValue.split('.')
                                    const decimals = parts[1] || ''
                                    if (decimals.length === 1) {
                                        this.displayValue = strValue
                                    } else {
                                        this.displayValue = strValue
                                    }
                                } else {
                                    this.displayValue = strValue
                                }
                            } else {
                                this.displayValue = ''
                            }
                        } catch (e) {
                            this.displayValue = ''
                        }
                    } else {
                        this.displayValue = ''
                    }
                }
            }
        }
    },
    methods: {
        restrictInput(e) {
            const value = this.displayValue || ''
            // console.log('value: ', value)
            // Allow control keys regardless
            if (e.key === 'Backspace' || e.key === 'Delete' ||
                e.key === 'ArrowLeft' || e.key === 'ArrowRight' ||
                e.key === 'Tab') {
                return
            }

            // Prevent typing non-numeric characters (except decimal point)
            if (!/^\d$/.test(e.key) && e.key !== '.') {
                e.preventDefault()
                return
            }

            // Prevent typing a second decimal point
            if (e.key === '.' && value.includes('.')) {
                e.preventDefault()
                return
            }

            // Handle decimal part
            if (value.includes('.')) {
                const parts = value.split('.')
                const decimals = parts[1]

                // Prevent typing more than 2 decimal places
                if (decimals.length >= 2 && e.target.selectionStart > value.indexOf('.')) {
                    e.preventDefault()
                    return
                }

            } else {
                // If empty, allow 0, decimal point, or any digit
                if (value === '' && e.key === '0')
                    return // Allow 0 at the start
            }
        },
        handleInput(value) {
            // This handles pasting and other input methods that bypass keypress
            if (!value) {
                this.displayValue = ''
                this.$emit('input', '')
                return
            }

            // Filter to only allow digits and a single decimal point
            let filtered = value.replace(/[^\d.]/g, '')

            // Ensure only one decimal point
            if ((filtered.match(/\./g) || []).length > 1) {
                const parts = filtered.split('.')
                filtered = parts[0] + '.' + parts.slice(1).join('')
            }

            // Handle decimal portion
            if (filtered.includes('.')) {
                const parts = filtered.split('.')
                let wholeNumber = parts[0]
                const decimals = parts[1].substring(0, 2) // Limit to 2 decimal places

                // Special case for numbers starting with decimal point
                if (wholeNumber === '') {
                    filtered = '.' + decimals
                } else {
                    // Remove leading zeros from whole number
                    // wholeNumber = wholeNumber.replace(/^0+/, '');

                    // If we stripped all zeros and have nothing left
                    if (wholeNumber === '') {
                        // Special case: if input was like "0.xx"
                        filtered = '.' + decimals
                    } else {
                        filtered = wholeNumber + '.' + decimals
                    }
                }
            } else {
                // No decimal point - this is a whole number
                // Remove leading zeros
                filtered = filtered.replace(/^0+/, '')
            }

            this.displayValue = filtered
            this.$emit('input', filtered)

            // Update error message
            const pattern = /^([1-9]\d*(\.\d{1,2})?|(\.\d{1,2}))$/
            if (filtered && !pattern.test(filtered)) {
                if (filtered.includes('.')) {
                    if (filtered.startsWith('.')) {
                        if (filtered.length === 1) {
                            this.errorMessage = 'Add at least one digit after decimal point'
                        } else if (filtered.length > 3) {
                            this.errorMessage = 'Maximum 2 decimal places allowed'
                        } else {
                            this.errorMessage = ''
                        }
                    } else if (filtered.split('.')[1].length > 2) {
                        this.errorMessage = 'Maximum 2 decimal places allowed'
                    } else {
                        this.errorMessage = ''
                    }
                } else if (filtered === '') {
                    this.errorMessage = ''
                } else {
                    this.errorMessage = ''
                }
            } else {
                this.errorMessage = ''
            }
        },
        handleBlur() {
            // On blur, ensure format is correct
            const value = this.displayValue

            if (!value) {
                this.displayValue = ''
                this.$emit('input', '')
                return
            }

            // Try to format the value
            try {
                // If it's a whole number (no decimal), keep it as is
                if (!value.includes('.')) {
                    // Just validate it's a proper whole number
                    if (/^[1-9]\d*$/.test(value)) {
                        // Valid whole number
                        this.errorMessage = ''
                    } else {
                        this.errorMessage = 'Must start with a non-zero digit'
                    }
                    return
                }

                // Handle numbers starting with decimal point
                if (value.startsWith('.')) {
                    const decimals = value.substring(1)
                    if (decimals.length === 1) {
                        // Add a zero to make it 2 decimal places
                        this.displayValue = '.' + decimals + '0'
                        this.$emit('input', this.displayValue)
                    }
                    this.errorMessage = ''
                    return
                }

                // If we have a decimal point, format appropriately
                const parts = value.split('.')
                let wholeNumber = parts[0]
                let decimals = parts[1] || ''

                // If we have 1 decimal place, pad to 2
                if (decimals.length === 1) {
                    this.displayValue = wholeNumber + '.' + decimals + '0'
                    this.$emit('input', this.displayValue)
                }

                this.errorMessage = ''
            } catch (e) {
                this.displayValue = ''
                this.$emit('input', '')
                this.errorMessage = 'Invalid input'
            }
        }
    }
}
  </script>
